import React, { useCallback, useEffect, useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { makeGETRequestOnUserService, makePOSTRequestOnUserService, makePatchRequestOnUserService } from "../../Api/api";
import styles from "./mutual-funds.module.css";

export interface MFInvestorStatus {
    investor_profile: boolean;
    file: boolean;
    bank_account: boolean;
    demat?: boolean;
    demat_account?: boolean;
    address: boolean;
    email_address: boolean;
    phone_number: boolean;
    nominee: boolean;
    investment_account: boolean;
    kyc_request_creation: boolean;
    signature: boolean;
    identity_document: boolean;
    esign: boolean;
    are_MF_KYC_Completed: boolean;
}

export interface MFNomineeDetails {
    date_of_birth?: string | Date | null;
}

const CELEBRATION_SPARKLES = [
    { top: "4%", left: "50%", size: 12, delay: "0s" },
    { top: "20%", left: "92%", size: 9, delay: "0.4s" },
    { top: "70%", left: "96%", size: 11, delay: "0.9s" },
    { top: "94%", left: "58%", size: 10, delay: "0.3s" },
    { top: "88%", left: "8%", size: 9, delay: "1.1s" },
    { top: "24%", left: "2%", size: 11, delay: "0.7s" },
];

const SparkleIcon = () => (
    <svg viewBox="0 0 24 24" role="presentation" focusable="false">
        <path d="M12 0 14 10 24 12 14 14 12 24 10 14 0 12 10 10Z" />
    </svg>
);

const ProfileIcon = () => (
    <svg viewBox="0 0 24 24" role="presentation" focusable="false">
        <circle cx="12" cy="8" r="3.2" />
        <path d="M5.5 19c1-3.2 3.7-5 6.5-5s5.5 1.8 6.5 5" />
    </svg>
);

const ShieldIcon = () => (
    <svg viewBox="0 0 24 24" role="presentation" focusable="false">
        <path d="M12 3.5 19 6.5v5c0 4.5-3 7.7-7 8.5-4-.8-7-4-7-8.5v-5Z" />
        <path d="M9 12l2 2 4-4" />
    </svg>
);

const BankIcon = () => (
    <svg viewBox="0 0 24 24" role="presentation" focusable="false">
        <path d="M4 10 12 4l8 6" />
        <path d="M5.5 10v9M18.5 10v9M9 10v9M15 10v9" />
        <path d="M3.5 19h17" />
    </svg>
);

const IdIcon = () => (
    <svg viewBox="0 0 24 24" role="presentation" focusable="false">
        <rect x="3.5" y="6" width="17" height="13" rx="2" />
        <circle cx="9" cy="12" r="2" />
        <path d="M13.5 10.5h4M13.5 13.5h4M6.5 16.5h11" />
    </svg>
);

const PenIcon = () => (
    <svg viewBox="0 0 24 24" role="presentation" focusable="false">
        <path d="M4 20h4L18 10l-4-4L4 16v4Z" />
        <path d="M13 7l4 4" />
    </svg>
);

type MFKycFlowStepKey =
    | "investor_profile"
    | "mf_files"
    | "bank_account"
    | "demat_account"
    | "address"
    | "email_address"
    | "phone_number"
    | "nominee"
    | "investment_account"
    | "create_request"
    | "signature"
    | "identity_document"
    | "esign";
type MFReadinessStepState = "pending" | "running" | "complete" | "error";
type KycDialogMessageTone = "error" | "info";

class KycFlowStepError extends Error {
    stepKey: MFKycFlowStepKey;

    constructor(message: string, stepKey: MFKycFlowStepKey) {
        super(message);
        this.name = "KycFlowStepError";
        this.stepKey = stepKey;
    }
}

const KYC_STEP_DELAY_MS = 1000;
/*
 * A KRA account can come back from pre-verification without being investment
 * ready, and sometimes without an explicit reason string. Checking only for
 * reasons let that case through, and the flow then failed further along where
 * the cause was no longer obvious.
 */
const MF_KRA_NOT_READY_MESSAGE =
    "This investor's KRA account is not investment ready. Their relationship manager needs to resolve it before MF onboarding can continue.";
const NOMINEE_AADHAAR_LAST_FOUR_REGEX = /^\d{4}$/;
const NOMINEE_PHONE_REGEX = /^\d{10}$/;
const NOMINEE_PIN_CODE_REGEX = /^\d{6}$/;
const NOMINEE_GUARDIAN_NAME_REGEX = /^[A-Za-z .'-]{2,}$/;
const NOMINEE_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface MFKycRequest {
    id?: string;
    kyc_form_id?: string;
    kyc_request_id?: string;
    status?: string | null;
    reason?: string | null;
    proof_details?: {
        fetch_url?: string | null;
        status?: string | null;
    } | null;
    requirements?: {
        fields_needed?: string[] | null;
    } | null;
    signature_provided?: boolean | null;
    esign_details?: {
        esign_url?: string | null;
        status?: string | null;
    } | null;
    data?: MFKycRequest;
}

interface MFPreVerificationResponse {
    id?: string;
    pre_verification_id?: string;
    status?: string | null;
    reason?: string | null;
    data?: MFPreVerificationResponse;
}

interface MFNomineeFormValues {
    aadhaarNumber: string;
    guardianName: string;
    emailAddress: string;
    phoneNumber: string;
    address: string;
    pinCode: string;
}

interface MFKycAction {
    label: string;
    url?: string;
}

interface KycPopup {
    title: string;
    url: string;
    opened: boolean;
    kind: "signature" | "esign";
    kycFormId?: string;
}

const kycSteps: Array<{
    key: MFKycFlowStepKey;
    endpoint: string;
    title: string;
    description: string;
    flag?: keyof Omit<MFInvestorStatus, "are_MF_KYC_Completed">;
}> = [
        {
            key: "investor_profile",
            endpoint: "mutual_fund/investor_profile/create/",
            title: "Investor profile",
            description: "Checking your basic investor details.",
            flag: "investor_profile",
        },
        {
            key: "mf_files",
            endpoint: "mutual_fund/mf_files/create/",
            title: "MF files",
            description: "Preparing files required for mutual fund KYC.",
            flag: "file",
        },
        {
            key: "bank_account",
            endpoint: "mutual_fund/bank_account/create/",
            title: "Bank account",
            description: "Confirming your bank account for mutual fund investing.",
            flag: "bank_account",
        },
        {
            key: "demat_account",
            endpoint: "mutual_fund/demat_account/create/",
            title: "Demat account",
            description: "Confirming your Demat account for mutual fund investing.",
            flag: "demat_account",
        },
        {
            key: "address",
            endpoint: "mutual_fund/address/create/",
            title: "Registered address",
            description: "Checking your registered address details.",
            flag: "address",
        },
        {
            key: "email_address",
            endpoint: "mutual_fund/email_address/create/",
            title: "Email verification",
            description: "Checking the email address linked to your account.",
            flag: "email_address",
        },
        {
            key: "phone_number",
            endpoint: "mutual_fund/phone_number/create/",
            title: "Mobile verification",
            description: "Checking the mobile number linked to your account.",
            flag: "phone_number",
        },
        {
            key: "nominee",
            endpoint: "mutual_fund/nominee/create/",
            title: "Nominee details",
            description: "Confirming nominee details for the mutual fund account.",
            flag: "nominee",
        },
        {
            key: "investment_account",
            endpoint: "mutual_fund/investment_account/create/",
            title: "Investment account",
            description: "Creating your mutual fund investment account.",
            flag: "investment_account",
        },
        {
            key: "create_request",
            endpoint: "mutual_fund/kyc_form/create/",
            title: "Create KYC form",
            description: "Starting your mutual fund KYC form.",
            flag: "kyc_request_creation",
        },
        {
            key: "signature",
            endpoint: "mutual_fund/mf_signature/create/",
            title: "Signature upload",
            description: "Preparing the signature step required for mutual fund KYC.",
            flag: "signature",
        },
        {
            key: "identity_document",
            endpoint: "mutual_fund/kyc_form/document_status/update/",
            title: "Approve document",
            description: "Checking whether document approval is ready for eSign.",
            flag: "identity_document",
        },
        {
            key: "esign",
            endpoint: "mutual_fund/kyc_form/document_status/update/",
            title: "Complete eSign",
            description: "Checking whether Aadhaar eSign has been completed.",
            flag: "esign",
        },
    ];

const MF_KYC_POPUP_MESSAGE = "MF_KYC_POPUP_COMPLETE";
const MF_KYC_POPUP_ALLOWED_ORIGINS = [
    "https://smest.in",
    "https://www.smest.in",
    "https://staging.smest.in",
    "https://staging-api.smestbonds.com",
];

// The 13 raw API steps above are grouped into 5 user-facing phases for
// display — showing all 11 as a flat list at once is exactly the kind of
// high-cognitive-load stepper UX guidance advises against. Only the phase
// currently in progress is "spotlighted"; the rail above it shows overall
// progress across phases.
type KycPhaseKey = "profile" | "verification" | "account" | "identity" | "esign";

const KYC_PHASES: Array<{
    key: KycPhaseKey;
    title: string;
    description: string;
    steps: MFKycFlowStepKey[];
    icon: React.ReactNode;
}> = [
        {
            key: "profile",
            title: "Verifying your profile",
            description: "Checking your investor details and preparing the files mutual fund KYC needs.",
            steps: ["investor_profile", "mf_files"],
            icon: <ProfileIcon />,
        },
        {
            key: "verification",
            title: "Confirming your details",
            description: "Validating bank, demat, address, email, mobile and nominee details.",
            steps: [
                "bank_account",
                "demat_account",
                "address",
                "email_address",
                "phone_number",
                "nominee",
            ],
            icon: <ShieldIcon />,
        },
        {
            key: "account",
            title: "Setting up your investment account",
            description: "Creating your mutual fund investment account and KYC request.",
            steps: ["investment_account", "create_request"],
            icon: <BankIcon />,
        },
        {
            key: "identity",
            title: "Signature verification",
            description: "Uploading your signature and approving the KYC document.",
            steps: ["signature", "identity_document"],
            icon: <IdIcon />,
        },
        {
            key: "esign",
            title: "Finalizing with e-Sign",
            description: "Checking document approval and completing Aadhaar e-Sign.",
            steps: ["esign"],
            icon: <PenIcon />,
        },
    ];

function getPhaseState(
    phase: (typeof KYC_PHASES)[number],
    stepStates: Record<MFKycFlowStepKey, MFReadinessStepState>,
): MFReadinessStepState {
    const states = phase.steps.map((key) => stepStates[key]);
    if (states.some((s) => s === "error")) return "error";
    if (states.every((s) => s === "complete")) return "complete";
    if (states.some((s) => s === "running")) return "running";
    return "pending";
}

function getInitialReadinessState(
    investorStatus?: MFInvestorStatus,
): Record<MFKycFlowStepKey, MFReadinessStepState> {
    return kycSteps.reduce(
        (state, step) => {
            state[step.key] = isReadinessFlagComplete(investorStatus, step.flag)
                ? "complete"
                : "pending";
            return state;
        },
        {} as Record<MFKycFlowStepKey, MFReadinessStepState>,
    );
}

function getReadinessFlagState(investorStatus?: MFInvestorStatus) {
    return kycSteps.reduce(
        (state, step) => {
            if (isReadinessFlagComplete(investorStatus, step.flag)) {
                state[step.key] = "complete";
            }
            return state;
        },
        {} as Partial<Record<MFKycFlowStepKey, MFReadinessStepState>>,
    );
}

function isReadinessFlagComplete(
    investorStatus: MFInvestorStatus | undefined,
    flag: keyof Omit<MFInvestorStatus, "are_MF_KYC_Completed"> | undefined,
) {
    if (!flag || !investorStatus) return false;
    if (flag === "demat_account") {
        return investorStatus.demat === true || investorStatus.demat_account === true;
    }
    return investorStatus[flag] === true;
}

function getReadableApiError(error: unknown) {
    if (
        typeof error === "object" &&
        error !== null &&
        "response" in error &&
        typeof error.response === "object" &&
        error.response !== null &&
        "data" in error.response
    ) {
        const data = error.response.data as {
            error?: string | { message?: string; details?: Array<{ message?: string }> };
            detail?: string;
            message?: string;
        };
        if (typeof data.error === "object") {
            const fieldMessage = data.error.details?.find(Boolean)?.message;
            return fieldMessage ?? data.error.message ?? "The API returned an error.";
        }
        if (typeof data.error === "string") return data.error;
        if (typeof data.message === "string") return data.message;
        if (typeof data.detail === "string") return data.detail;
    }
    if (error instanceof Error && error.message) return error.message;
    return "The mutual fund KYC flow could not be completed. Please retry the readiness check.";
}

function getObjectRecord(source: unknown): Record<string, unknown> | undefined {
    if (!source || typeof source !== "object" || Array.isArray(source)) {
        return undefined;
    }
    return source as Record<string, unknown>;
}

function getPayloadRecord(source: unknown): Record<string, unknown> | undefined {
    const record = getObjectRecord(source);
    const nestedData = getObjectRecord(record?.data);
    return nestedData ?? record;
}

function getStringField(source: unknown, keys: string[]): string | undefined {
    const record = getObjectRecord(source);
    if (!record) return undefined;

    for (const key of keys) {
        const value = record[key];
        if (typeof value === "string" && value.trim()) return value;
    }

    for (const value of Object.values(record)) {
        const nestedValue = getStringField(value, keys);
        if (nestedValue) return nestedValue;
    }

    return undefined;
}

function prettifyReasonPath(path: string) {
    return path
        .replace(/\[\d+\]/g, "")
        .split(".")
        .filter((segment) => segment && segment !== "data")
        .pop()
        ?.replace(/_/g, " ");
}

function collectReasonMessages(
    source: unknown,
    path = "",
    seen = new Set<unknown>(),
): string[] {
    if (!source || typeof source !== "object" || seen.has(source)) return [];
    seen.add(source);

    if (Array.isArray(source)) {
        return source.flatMap((item, index) =>
            collectReasonMessages(item, `${path}[${index}]`, seen),
        );
    }

    const record = source as Record<string, unknown>;
    const messages: string[] = [];
    if (typeof record.reason === "string" && record.reason.trim()) {
        const label = prettifyReasonPath(path);
        messages.push(
            label ? `${label}: ${record.reason.trim()}` : record.reason.trim(),
        );
    }

    Object.entries(record).forEach(([key, value]) => {
        if (key === "reason") return;
        messages.push(
            ...collectReasonMessages(value, path ? `${path}.${key}` : key, seen),
        );
    });

    return Array.from(new Set(messages));
}

function getNestedStringField(source: unknown, path: string[]): string | undefined {
    const candidates = [getObjectRecord(source), getPayloadRecord(source)].filter(
        Boolean,
    ) as Record<string, unknown>[];

    for (const candidate of candidates) {
        let current: unknown = candidate;
        for (const segment of path) {
            current = getObjectRecord(current)?.[segment];
        }
        if (typeof current === "string" && current.trim()) return current;
    }

    return undefined;
}

function getKycFormId(response: MFKycRequest) {
    return getStringField(response, ["kyc_form_id", "kyc_request_id", "id"]);
}

function getPreVerificationId(response: MFPreVerificationResponse) {
    return getStringField(response, ["pre_verification_id", "id"]);
}

function getPreVerificationStatus(response: MFPreVerificationResponse) {
    return getNestedStringField(response, ["status"])?.toLowerCase();
}

function getPreVerificationReadinessStatus(response: MFPreVerificationResponse) {
    return getNestedStringField(response, ["readiness", "status"])?.toLowerCase();
}

/** Both halves have to land: the check completed AND the KRA is verified. */
function isPreVerificationCompliant(response: MFPreVerificationResponse) {
    return (
        getPreVerificationStatus(response) === "completed" &&
        getPreVerificationReadinessStatus(response) === "verified"
    );
}

function getPreVerificationFailureMessage(response: MFPreVerificationResponse) {
    const reasons = collectReasonMessages(response);
    return reasons.length > 0
        ? `${MF_KRA_NOT_READY_MESSAGE} Reason: ${reasons.join(". ")}`
        : MF_KRA_NOT_READY_MESSAGE;
}

function getKycRequestStatus(response: MFKycRequest) {
    return getNestedStringField(response, ["status"])?.toLowerCase();
}

function getKycRequestReason(response: MFKycRequest) {
    return getNestedStringField(response, ["reason"]);
}

function getProofStatus(response: MFKycRequest) {
    return getNestedStringField(response, ["proof_details", "status"])?.toLowerCase();
}

function getProofFetchUrl(response: MFKycRequest) {
    return getNestedStringField(response, ["proof_details", "fetch_url"]);
}

function getEsignStatus(response: MFKycRequest) {
    return getNestedStringField(response, ["esign_details", "status"])?.toLowerCase();
}

function getEsignUrl(response: MFKycRequest) {
    return getNestedStringField(response, ["esign_details", "esign_url"]);
}

function getRequirementsFieldsNeeded(response: MFKycRequest) {
    const fieldsNeeded = getObjectRecord(getPayloadRecord(response)?.requirements)
        ?.fields_needed;
    return Array.isArray(fieldsNeeded)
        ? fieldsNeeded.filter((field): field is string => typeof field === "string")
        : [];
}

function isSignatureProvided(response: MFKycRequest) {
    const signatureProvided = getPayloadRecord(response)?.signature_provided;
    if (typeof signatureProvided === "boolean") return signatureProvided;

    const fieldsNeeded = getRequirementsFieldsNeeded(response);
    return fieldsNeeded.length > 0 && !fieldsNeeded.includes("signature");
}

function getExternalActionUrl(response: unknown) {
    return getStringField(response, [
        "fetch_url",
        "esign_url",
        "redirect_url",
        "token_url",
        "forward_to_url",
        "external_url",
        "url",
    ]);
}

function getKycCallbackUrl(step: KycPopup["kind"]) {
    if (typeof window === "undefined") return "";
    const url = new URL(window.location.href);
    url.searchParams.set("mf_kyc_popup", "1");
    url.searchParams.set("mf_kyc_step", step);
    return url.toString();
}

function ensureKycRequestCanProceed(
    response: MFKycRequest,
    failedStepKey: MFKycFlowStepKey = "create_request",
) {
    const status = getKycRequestStatus(response);
    const reason = getKycRequestReason(response);

    if (status === "failed" && reason) {
        throw new KycFlowStepError(reason, failedStepKey);
    }
}

function isSignatureUploaded(response: MFKycRequest) {
    return Boolean(
        isSignatureProvided(response) &&
        getKycRequestStatus(response) === "created" &&
        getProofStatus(response) === "pending"
    );
}

function shouldSkipSignatureUpload(response?: MFKycRequest) {
    return Boolean(
        response &&
        (isSignatureProvided(response) ||
            isSignatureUploaded(response) ||
            isDocumentApproved(response) ||
            isEsignSubmitted(response)),
    );
}

function updateKycFormDocumentStatus(
    kycFormId: string,
    kycStatus: string | undefined,
    payload: { identity_document_completed?: boolean; esign_completed?: boolean },
) {
    return makePatchRequestOnUserService(
        "/mutual_fund/kyc_form/document_status/update/",
        {
            kyc_form_id: kycFormId,
            kyc_status: kycStatus ?? "",
            ...payload,
        }
    );
}

function isDocumentApproved(response: MFKycRequest) {
    return Boolean(
        getKycRequestStatus(response) === "awaiting_esign" &&
        getEsignStatus(response) === "pending"
    );
}

function isEsignSubmitted(response: MFKycRequest) {
    return Boolean(
        getKycRequestStatus(response) === "submitted" &&
        getEsignStatus(response) === "successful"
    );
}

function isNomineeMinor(dateOfBirth?: string | Date | null) {
    if (!dateOfBirth) return false;
    const dob = new Date(dateOfBirth);
    if (Number.isNaN(dob.getTime())) return false;

    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
        age -= 1;
    }
    return age < 18;
}

function getDigitsOnly(value: string, maxLength: number) {
    return value.replace(/\D/g, "").slice(0, maxLength);
}

function getValidatedNomineeForm(
    nomineeForm: MFNomineeFormValues,
    nomineeIsMinor: boolean,
) {
    const values = {
        aadhaarNumber: nomineeForm.aadhaarNumber.trim(),
        guardianName: nomineeForm.guardianName.trim(),
        emailAddress: nomineeForm.emailAddress.trim(),
        phoneNumber: nomineeForm.phoneNumber.trim(),
        address: nomineeForm.address.trim(),
        pinCode: nomineeForm.pinCode.trim(),
    };

    if (!NOMINEE_AADHAAR_LAST_FOUR_REGEX.test(values.aadhaarNumber)) {
        return { values, error: "Please enter the last 4 digits of nominee Aadhaar." };
    }
    if (nomineeIsMinor && !NOMINEE_GUARDIAN_NAME_REGEX.test(values.guardianName)) {
        return { values, error: "Please enter a valid guardian name." };
    }
    if (!NOMINEE_EMAIL_REGEX.test(values.emailAddress)) {
        return { values, error: "Please enter a valid nominee email address." };
    }
    if (!NOMINEE_PHONE_REGEX.test(values.phoneNumber)) {
        return { values, error: "Please enter a valid 10 digit nominee phone number." };
    }
    if (!values.address) {
        return { values, error: "Please enter the nominee address." };
    }
    if (!NOMINEE_PIN_CODE_REGEX.test(values.pinCode)) {
        return { values, error: "Please enter a valid 6 digit PIN code." };
    }
    return { values, error: null };
}

function openVerificationWindow(url: string) {
    const width = 960;
    const height = 760;
    const left = Math.max(0, window.screenX + (window.outerWidth - width) / 2);
    const top = Math.max(0, window.screenY + (window.outerHeight - height) / 2);
    const popup = window.open(
        url,
        "smest_mf_kyc_verification",
        [
            "popup=yes",
            `width=${width}`,
            `height=${height}`,
            `left=${Math.round(left)}`,
            `top=${Math.round(top)}`,
            "resizable=yes",
            "scrollbars=yes",
        ].join(","),
    );

    popup?.focus();
    return popup;
}

function openKycExternalStep(url: string, title: string, kind: KycPopup["kind"]) {
    const openedWindow = openVerificationWindow(url);
    return {
        openedWindow,
        popup: {
            title,
            url,
            opened: !!openedWindow,
            kind,
        },
    };
}

function wait(ms: number) {
    return new Promise((resolve) => {
        window.setTimeout(resolve, ms);
    });
}

interface KycCheckDialogProps {
    open: boolean;
    userUuid: string;
    investorStatus?: MFInvestorStatus;
    nomineeDetails?: MFNomineeDetails | null;
    onClose: () => void;
    onRefreshInvestorStatus: () => Promise<MFInvestorStatus | undefined>;
    onComplete: () => void;
}

const KycCheckDialog = ({
    open,
    userUuid,
    investorStatus,
    nomineeDetails,
    onClose,
    onRefreshInvestorStatus,
    onComplete,
}: KycCheckDialogProps) => {
    const [stepStates, setStepStates] = useState(() =>
        getInitialReadinessState(investorStatus),
    );
    const [errorMessage, setErrorMessage] = useState("");
    const [messageTone, setMessageTone] = useState<KycDialogMessageTone>("error");
    const [errorStepKey, setErrorStepKey] = useState<MFKycFlowStepKey | null>(
        null,
    );
    const [action, setAction] = useState<MFKycAction | null>(null);
    const [popup, setPopup] = useState<KycPopup | null>(null);
    const [isNomineeFormOpen, setIsNomineeFormOpen] = useState(false);
    const [nomineeForm, setNomineeForm] = useState<MFNomineeFormValues>({
        aadhaarNumber: "",
        guardianName: "",
        emailAddress: "",
        phoneNumber: "",
        address: "",
        pinCode: "",
    });
    const [isMfKycComplete, setIsMfKycComplete] = useState(false);
    const runIdRef = useRef(0);
    const investorStatusRef = useRef(investorStatus);
    const nomineeDetailsRef = useRef(nomineeDetails);
    const nomineeSubmissionRef = useRef<MFNomineeFormValues | null>(null);
    const refreshInvestorStatusRef = useRef(onRefreshInvestorStatus);
    const popupWindowRef = useRef<Window | null>(null);

    const setStepState = useCallback((
        key: MFKycFlowStepKey,
        state: MFReadinessStepState,
    ) => {
        setStepStates((current) => ({
            ...current,
            [key]: state,
        }));
    }, []);

    const runStep = useCallback(async <T,>(
        key: MFKycFlowStepKey,
        callback: () => Promise<T>,
    ) => {
        setStepState(key, "running");
        try {
            await wait(KYC_STEP_DELAY_MS);
            const result = await callback();
            setStepState(key, "complete");
            return result;
        } catch (error) {
            setStepState(key, "error");
            setErrorStepKey(key);
            throw error;
        }
    }, [setStepState]);

    useEffect(() => {
        investorStatusRef.current = investorStatus;
        setStepStates((current) => ({
            ...current,
            ...getReadinessFlagState(investorStatus),
        }));
    }, [investorStatus]);

    useEffect(() => {
        nomineeDetailsRef.current = nomineeDetails;
    }, [nomineeDetails]);

    useEffect(() => {
        refreshInvestorStatusRef.current = onRefreshInvestorStatus;
    }, [onRefreshInvestorStatus]);

    useEffect(() => {
        if (typeof window === "undefined") return;

        const params = new URLSearchParams(window.location.search);
        if (params.get("mf_kyc_popup") !== "1" || !window.opener) return;

        window.opener.postMessage(
            {
                type: MF_KYC_POPUP_MESSAGE,
                step: params.get("mf_kyc_step"),
            },
            "*",
        );
        window.close();
    }, []);

    function isAlreadyExistsError(error: unknown): boolean {
        if (
            typeof error === "object" &&
            error !== null &&
            "response" in error &&
            typeof error.response === "object" &&
            error.response !== null &&
            "data" in error.response
        ) {
            const data = error.response.data as Record<string, unknown>;
            const errorMsg =
                (typeof data.error === "string" ? data.error : "") ||
                (typeof data.message === "string" ? data.message : "") ||
                (typeof data.detail === "string" ? data.detail : "") ||
                "";
            const lowerMsg = errorMsg.toLowerCase();
            return (
                lowerMsg.includes("already exists") ||
                lowerMsg.includes("already created") ||
                lowerMsg.includes("duplicate") ||
                lowerMsg.includes("already been created")
            );
        }
        return false;
    }

    const runReadinessChecks = useCallback(async () => {
        const runId = ++runIdRef.current;

        try {
            const latestStatus = await refreshInvestorStatusRef.current();
            if (runIdRef.current !== runId) return;

            const statusForRun = latestStatus ?? investorStatusRef.current;
            setStepStates(getInitialReadinessState(statusForRun));
            setErrorMessage("");
            setMessageTone("error");
            setErrorStepKey(null);
            setAction(null);
            setPopup(null);
            setIsMfKycComplete(statusForRun?.are_MF_KYC_Completed === true);

            if (statusForRun?.are_MF_KYC_Completed) {
                setStepStates(getInitialReadinessState(statusForRun));
                onComplete();
                return;
            }

            setStepState("investor_profile", "running");
            setErrorStepKey("investor_profile");
            setMessageTone("info");
            setErrorMessage("Checking whether this account can start MF KYC...");

            const preVerificationCreate = await makePOSTRequestOnUserService(
                "mutual_fund/pre_verification/create/",
                { user_uuid: userUuid },
            );
            if (runIdRef.current !== runId) return;

            const preVerificationCreateData = preVerificationCreate.data as MFPreVerificationResponse;
            const preVerificationCreateReasons = collectReasonMessages(preVerificationCreateData);
            if (getPreVerificationStatus(preVerificationCreateData) !== "accepted") {
                throw new KycFlowStepError(
                    preVerificationCreateReasons.join(". ") ||
                    "Pre-verification was not accepted. Please check the user details and try again.",
                    "investor_profile",
                );
            }

            const preVerificationId = getPreVerificationId(preVerificationCreateData);
            if (!preVerificationId) {
                throw new KycFlowStepError(
                    "Pre-verification id was not returned.",
                    "investor_profile",
                );
            }

            await wait(KYC_STEP_DELAY_MS);
            const preVerificationResponse = await makeGETRequestOnUserService(
                `/mutual_fund/pre_verification/get_by_id/${preVerificationId}/?user_uuid=${userUuid}`,
            );
            if (runIdRef.current !== runId) return;

            const preVerificationData = preVerificationResponse.data as MFPreVerificationResponse;
            if (!isPreVerificationCompliant(preVerificationData)) {
                throw new KycFlowStepError(
                    getPreVerificationFailureMessage(preVerificationData),
                    "investor_profile",
                );
            }

            setErrorMessage("");
            setMessageTone("error");
            setErrorStepKey(null);
            setStepState("investor_profile", "pending");

            const readinessSteps = kycSteps.filter(
                (step) =>
                    step.key !== "create_request" &&
                    step.key !== "signature" &&
                    step.key !== "identity_document" &&
                    step.key !== "esign",
            );

            for (const step of readinessSteps) {
                if (runIdRef.current !== runId) return;
                if (isReadinessFlagComplete(statusForRun, step.flag)) {
                    setStepState(step.key, "complete");
                    continue;
                }

                if (step.key === "nominee") {
                    const nomineeInput = nomineeSubmissionRef.current;
                    if (!nomineeInput) {
                        setStepState("nominee", "pending");
                        setErrorMessage("");
                        setErrorStepKey(null);
                        setIsNomineeFormOpen(true);
                        return;
                    }

                    const nomineeIsMinor = isNomineeMinor(
                        nomineeDetailsRef.current?.date_of_birth,
                    );
                    await runStep("nominee", async () => {
                        const createResponse = await makePOSTRequestOnUserService(
                            "mutual_fund/nominee/create/",
                            {
                                user_uuid: userUuid,
                                is_nominee_minor: nomineeIsMinor,
                                aadhaar_number: nomineeInput.aadhaarNumber,
                                ...(nomineeIsMinor
                                    ? { guardian_name: nomineeInput.guardianName }
                                    : {}),
                            },
                        );
                        const nomineeId = getStringField(createResponse.data, [
                            "nominee_id",
                            "id",
                        ]);
                        if (!nomineeId) {
                            throw new Error("Nominee id was not returned.");
                        }

                        await wait(KYC_STEP_DELAY_MS);
                        return makePatchRequestOnUserService(
                            "mutual_fund/nominee/update/",
                            {
                                nominee_id: nomineeId,
                                is_nominee_minor: nomineeIsMinor,
                                email_address: nomineeInput.emailAddress,
                                phone_number: nomineeInput.phoneNumber,
                                address: nomineeInput.address,
                                pin_code: nomineeInput.pinCode,
                            },
                        );
                    });

                    nomineeSubmissionRef.current = null;
                    const refreshedStatus = await refreshInvestorStatusRef.current();
                    if (runIdRef.current !== runId) return;
                    investorStatusRef.current = refreshedStatus ?? investorStatusRef.current;
                    setStepStates((current) => ({
                        ...current,
                        ...getReadinessFlagState(refreshedStatus),
                    }));

                    if (refreshedStatus?.nominee) {
                        setStepState("nominee", "complete");
                        continue;
                    }
                    throw new KycFlowStepError(
                        "Nominee details were submitted, but nominee readiness is still pending. Please check status again shortly.",
                        "nominee",
                    );
                }

                if (step.key === "investment_account") {
                    await runStep(step.key, async () =>
                        makePOSTRequestOnUserService(step.endpoint, {
                            user_uuid: userUuid,
                            is_nominee_minor: isNomineeMinor(
                                nomineeDetailsRef.current?.date_of_birth,
                            ),
                        }),
                    );
                    continue;
                }

                await runStep(step.key, async () => {
                    try {
                        return await makePOSTRequestOnUserService(step.endpoint, {
                            user_uuid: userUuid,
                        });
                    } catch (error) {
                        if (isAlreadyExistsError(error)) {
                            // "Already exists" is not an error - the resource is ready
                            return undefined;
                        }
                        throw error;
                    }
                });
            }

            const fetchKycRequest = async (
                withDelay = true,
                failedStepKey: MFKycFlowStepKey = "create_request",
            ) => {
                if (withDelay) await wait(KYC_STEP_DELAY_MS);
                const { data } = await makeGETRequestOnUserService(
                    `/mutual_fund/kyc_form/get_by_user/${userUuid}/`
                );
                ensureKycRequestCanProceed(data, failedStepKey);
                return data;
            };

            let kycFormId: string | undefined;
            let kycRequest: MFKycRequest = {
                id: "",
                kyc_form_id: "",
                kyc_request_id: "",
                status: "",
                reason: "",
                proof_details: {},
                requirements: {},
                signature_provided: false,
                esign_details: {},
                data: {},
            };
            if (statusForRun?.kyc_request_creation) {
                setStepState("create_request", "complete");
                if (!statusForRun?.signature || !statusForRun?.identity_document || !statusForRun?.esign) {
                    kycRequest = await fetchKycRequest();
                    if (runIdRef.current !== runId) return;
                    kycFormId = getKycFormId(kycRequest);
                }
            } else {
                const createResponse = await runStep("create_request", async () => {
                    try {
                        return await makePOSTRequestOnUserService("mutual_fund/kyc_form/create/", {
                            user_uuid: userUuid,
                            proof_details_callback_url: getKycCallbackUrl("signature"),
                            esign_callback_url: getKycCallbackUrl("esign"),
                        });
                    } catch (error) {
                        if (isAlreadyExistsError(error)) {
                            return { data: {} };
                        }
                        throw error;
                    }
                });
                if (runIdRef.current !== runId) return;
                ensureKycRequestCanProceed(createResponse.data, "create_request");
                kycFormId = getKycFormId(createResponse.data);
                kycRequest = await fetchKycRequest(true, "create_request");
                if (runIdRef.current !== runId) return;
                kycFormId = getKycFormId(kycRequest) ?? kycFormId;
            }

            if (
                (!statusForRun?.signature || !statusForRun?.identity_document || !statusForRun?.esign) &&
                !kycFormId
            ) {
                throw new Error("KYC form id was not returned.");
            }

            if (statusForRun?.signature || shouldSkipSignatureUpload(kycRequest)) {
                setStepState("signature", "complete");
            } else {
                const signatureResponse = await runStep("signature", async () =>
                    makePOSTRequestOnUserService("mutual_fund/mf_signature/create/", {
                        kyc_form_id: kycFormId,
                    }),
                );
                if (runIdRef.current !== runId) return;

                kycRequest = await fetchKycRequest(true, "signature");
                if (runIdRef.current !== runId) return;

                const signatureUrl =
                    getProofFetchUrl(kycRequest) ?? getExternalActionUrl(signatureResponse.data);

                if (signatureUrl) {
                    const { openedWindow, popup: signaturePopup } = openKycExternalStep(
                        signatureUrl,
                        "Signature verification",
                        "signature",
                    );
                    popupWindowRef.current = openedWindow;
                    setStepState("identity_document", "error");
                    setErrorStepKey("identity_document");
                    setErrorMessage(
                        openedWindow
                            ? "Signature and document approval opened in a secure popup window. Complete it there, then check status again."
                            : "Signature and document approval is pending. Please allow popups and reopen verification.",
                    );
                    setAction({
                        label: "Continue verification",
                        url: signatureUrl,
                    });
                    setPopup({
                        ...signaturePopup,
                        kycFormId,
                    });
                    return;
                }

                if (
                    isSignatureUploaded(kycRequest) ||
                    isDocumentApproved(kycRequest) ||
                    isEsignSubmitted(kycRequest)
                ) {
                    setStepState("signature", "complete");
                } else {
                    setStepState("signature", "error");
                    setErrorStepKey("signature");
                    setErrorMessage(
                        "Signature verification is pending. Complete the external verification, then check status again.",
                    );
                    return;
                }
            }

            if (statusForRun?.identity_document) {
                setStepState("identity_document", "complete");
            } else {
                kycRequest = await runStep("identity_document", async () =>
                    fetchKycRequest(false, "identity_document"),
                );
                if (runIdRef.current !== runId) return;

                if (isDocumentApproved(kycRequest) || isEsignSubmitted(kycRequest)) {
                    await updateKycFormDocumentStatus(
                        kycFormId as string,
                        getKycRequestStatus(kycRequest),
                        { identity_document_completed: true },
                    );
                    if (runIdRef.current !== runId) return;
                    setStepState("identity_document", "complete");
                } else {
                    const proofFetchUrl = getProofFetchUrl(kycRequest);
                    await updateKycFormDocumentStatus(
                        kycFormId as string,
                        getKycRequestStatus(kycRequest),
                        {},
                    );
                    if (runIdRef.current !== runId) return;
                    setStepState("identity_document", "error");
                    setErrorStepKey("identity_document");
                    setErrorMessage(
                        isSignatureUploaded(kycRequest)
                            ? "Document approval is pending. Complete it in the external verification window, then check status again."
                            : "Document approval status is not ready yet. Please check status again shortly.",
                    );
                    if (proofFetchUrl) {
                        setAction({
                            label: "Continue verification",
                            url: proofFetchUrl,
                        });
                        setPopup({
                            title: "Signature verification",
                            url: proofFetchUrl,
                            opened: false,
                            kind: "signature",
                            kycFormId,
                        });
                    }
                    return;
                }
            }

            if (statusForRun?.esign) {
                setStepState("esign", "complete");
            } else {
                kycRequest = await runStep("esign", async () =>
                    fetchKycRequest(false, "esign"),
                );
                if (runIdRef.current !== runId) return;

                if (isEsignSubmitted(kycRequest)) {
                    await updateKycFormDocumentStatus(
                        kycFormId as string,
                        getKycRequestStatus(kycRequest),
                        { esign_completed: true },
                    );
                    if (runIdRef.current !== runId) return;
                    setStepState("esign", "complete");
                } else {
                    const esignUrl = getEsignUrl(kycRequest);
                    await updateKycFormDocumentStatus(
                        kycFormId as string,
                        getKycRequestStatus(kycRequest),
                        {},
                    );
                    if (runIdRef.current !== runId) return;
                    setStepState("esign", "error");
                    setErrorStepKey("esign");
                    setErrorMessage(
                        isDocumentApproved(kycRequest)
                            ? "Aadhaar eSign is pending. Complete it in the external verification window, then check status again."
                            : "Aadhaar eSign status is not ready yet. Please check status again shortly.",
                    );
                    if (esignUrl) {
                        const { openedWindow, popup: esignPopup } = openKycExternalStep(
                            esignUrl,
                            "Aadhaar eSign",
                            "esign",
                        );
                        popupWindowRef.current = openedWindow;
                        setAction({
                            label: "Continue eSign",
                            url: esignUrl,
                        });
                        setPopup({
                            ...esignPopup,
                            kycFormId,
                        });
                    }
                    return;
                }
            }
            if (runIdRef.current !== runId) return;

            const refreshedStatus = await refreshInvestorStatusRef.current();
            if (runIdRef.current !== runId) return;

            setStepStates((current) => ({
                ...current,
                ...getReadinessFlagState(refreshedStatus),
            }));
            setIsMfKycComplete(refreshedStatus?.are_MF_KYC_Completed === true);

            if (refreshedStatus?.are_MF_KYC_Completed) {
                onComplete();
                return;
            }
            setErrorMessage(
                "KYC details were submitted. Final readiness is still pending, please check status again shortly.",
            );
            setMessageTone("error");
            setErrorStepKey("esign");
            setStepState("esign", "error");
        } catch (error) {
            if (runIdRef.current !== runId) return;
            if (error instanceof KycFlowStepError) {
                setStepState(error.stepKey, "error");
                setErrorStepKey(error.stepKey);
            }
            setIsMfKycComplete(false);
            setMessageTone("error");
            setErrorMessage(getReadableApiError(error));
        }
    }, [onComplete, runStep, setStepState, userUuid]);

    const nomineeIsMinor = isNomineeMinor(nomineeDetails?.date_of_birth);

    const handleNomineeFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const { values, error } = getValidatedNomineeForm(
            nomineeForm,
            nomineeIsMinor,
        );

        if (error) {
            setStepState("nominee", "error");
            setErrorStepKey("nominee");
            setMessageTone("error");
            setErrorMessage(error);
            return;
        }

        nomineeSubmissionRef.current = values;
        setIsNomineeFormOpen(false);
        void runReadinessChecks();
    };

    const handleClose = () => {
        runIdRef.current += 1;
        popupWindowRef.current?.close();
        popupWindowRef.current = null;
        setPopup(null);
        setIsNomineeFormOpen(false);
        nomineeSubmissionRef.current = null;
        onClose();
    };

    const handlePopupCompleted = useCallback(async (closePanel = false) => {
        const currentPopup = popup;

        if (!currentPopup?.kycFormId) {
            setPopup(null);
            void runReadinessChecks();
            return;
        }

        const runId = ++runIdRef.current;
        const activeStep =
            currentPopup.kind === "esign" ? "esign" : "identity_document";
        setStepState(activeStep, "running");
        setErrorMessage("Checking latest KYC verification status...");
        setMessageTone("info");
        setErrorStepKey(activeStep);
        if (closePanel) {
            popupWindowRef.current?.close();
            popupWindowRef.current = null;
            setPopup(null);
        }

        try {
            await wait(KYC_STEP_DELAY_MS);
            const { data } = await makeGETRequestOnUserService(
                `mutual_fund/kyc_form/get_by_user/${userUuid}/`,
            );
            if (runIdRef.current !== runId) return;
            ensureKycRequestCanProceed(data, activeStep);

            if (isEsignSubmitted(data)) {
                await updateKycFormDocumentStatus(
                    currentPopup.kycFormId,
                    getKycRequestStatus(data),
                    { esign_completed: true },
                );
                if (runIdRef.current !== runId) return;
                setStepState("signature", "complete");
                setStepState("identity_document", "complete");
                setStepState("esign", "complete");
                setErrorMessage("");
                setMessageTone("error");
                setErrorStepKey(null);
                setAction(null);
                popupWindowRef.current?.close();
                popupWindowRef.current = null;
                setPopup(null);
                void runReadinessChecks();
                return;
            }

            if (isDocumentApproved(data)) {
                const esignUrl = getEsignUrl(data) ?? currentPopup.url;
                const shouldOpenEsign =
                    currentPopup.kind === "signature" && Boolean(getEsignUrl(data));
                let esignExternalStep: ReturnType<typeof openKycExternalStep> | null = null;
                if (currentPopup.kind === "signature") {
                    popupWindowRef.current?.close();
                    popupWindowRef.current = null;
                }
                if (shouldOpenEsign) {
                    esignExternalStep = openKycExternalStep(esignUrl, "Aadhaar eSign", "esign");
                }
                if (esignExternalStep?.openedWindow) {
                    popupWindowRef.current = esignExternalStep.openedWindow;
                }
                await updateKycFormDocumentStatus(
                    currentPopup.kycFormId,
                    getKycRequestStatus(data),
                    { identity_document_completed: true },
                );
                if (runIdRef.current !== runId) return;
                setStepState("signature", "complete");
                setStepState("identity_document", "complete");
                setStepState("esign", "error");
                setErrorStepKey("esign");
                setMessageTone("error");
                setErrorMessage(
                    "Aadhaar eSign is pending. Complete it in the external verification window, then check status again.",
                );
                setAction({
                    label: "Continue eSign",
                    url: esignUrl,
                });
                if (esignExternalStep || !closePanel) {
                    setPopup({
                        ...(esignExternalStep?.popup ?? currentPopup),
                        url: esignUrl,
                        kind: "esign",
                        kycFormId: currentPopup.kycFormId,
                    });
                }
                return;
            }

            if (isSignatureUploaded(data)) {
                const proofFetchUrl = getProofFetchUrl(data) ?? currentPopup.url;
                await updateKycFormDocumentStatus(
                    currentPopup.kycFormId,
                    getKycRequestStatus(data),
                    {},
                );
                if (runIdRef.current !== runId) return;
                setStepState("signature", "complete");
                setStepState("identity_document", "error");
                setErrorStepKey("identity_document");
                setMessageTone("error");
                setErrorMessage(
                    "Document approval is pending. Complete it in the external verification window, then check status again.",
                );
                setAction({
                    label: "Continue verification",
                    url: proofFetchUrl,
                });
                if (!closePanel) {
                    setPopup({ ...currentPopup, url: proofFetchUrl, kind: "signature" });
                }
                return;
            }

            const fallbackUrl =
                activeStep === "esign"
                    ? getEsignUrl(data) ?? currentPopup.url
                    : getProofFetchUrl(data) ?? currentPopup.url;
            setStepState(activeStep, "error");
            setMessageTone("error");
            setErrorMessage(
                "Verification is still pending. Complete the external verification, then check status again.",
            );
            setAction({
                label: activeStep === "esign" ? "Continue eSign" : "Continue verification",
                url: fallbackUrl,
            });
        } catch (error) {
            if (runIdRef.current !== runId) return;
            const failedStepKey =
                error instanceof KycFlowStepError ? error.stepKey : activeStep;
            setStepState(failedStepKey, "error");
            setMessageTone("error");
            setErrorMessage(getReadableApiError(error));
            setErrorStepKey(failedStepKey);
        }
    }, [popup, runReadinessChecks, setStepState, userUuid]);

    useEffect(() => {
        if (typeof window === "undefined") return;

        const handleMessage = (event: MessageEvent) => {
            if (
                event.origin !== window.location.origin &&
                !MF_KYC_POPUP_ALLOWED_ORIGINS.includes(event.origin)
            ) {
                return;
            }

            const data = event.data as { type?: string };
            if (data?.type !== MF_KYC_POPUP_MESSAGE) return;

            void handlePopupCompleted(true);
        };

        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, [handlePopupCompleted]);

    useEffect(() => {
        if (!popup?.opened) return;

        const intervalId = window.setInterval(() => {
            void handlePopupCompleted();
        }, 10000);

        return () => window.clearInterval(intervalId);
    }, [handlePopupCompleted, popup?.opened]);

    const completedSteps = kycSteps.filter(
        (step) => stepStates[step.key] === "complete",
    ).length;
    const isRunning = kycSteps.some((step) => stepStates[step.key] === "running");
    const isComplete =
        completedSteps === kycSteps.length && isMfKycComplete && !errorMessage;

    const phaseStates = KYC_PHASES.map((phase) => getPhaseState(phase, stepStates));
    const firstIncompletePhaseIndex = phaseStates.findIndex((s) => s !== "complete");
    const activePhaseIndex =
        firstIncompletePhaseIndex === -1 ? KYC_PHASES.length - 1 : firstIncompletePhaseIndex;
    const activePhase = KYC_PHASES[activePhaseIndex];
    const activePhaseState = phaseStates[activePhaseIndex];

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ className: styles.kycDialogPaper }}
            BackdropProps={{ className: styles.kycDialogBackdrop }}
            TransitionProps={{ onEntered: () => void runReadinessChecks() }}
        >
            <div className={styles.kycDialog}>
                <div className={styles.kycDialogGlow} aria-hidden="true" />
                <IconButton
                    aria-label="Close KYC check"
                    className={styles.kycDialogClose}
                    onClick={handleClose}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
                {isComplete ? (
                    <div className={styles.kycCelebration} role="status" aria-live="polite">
                        <div className={styles.kycCelebrationBadge}>
                            <span className={styles.kycCelebrationGlow} aria-hidden="true" />
                            <div className={styles.kycCelebrationSparkles} aria-hidden="true">
                                {CELEBRATION_SPARKLES.map((sparkle, i) => (
                                    <span
                                        key={i}
                                        className={styles.kycCelebrationSparkle}
                                        style={{
                                            top: sparkle.top,
                                            left: sparkle.left,
                                            width: sparkle.size,
                                            height: sparkle.size,
                                            ["--sparkle-delay" as string]: sparkle.delay,
                                        }}
                                    >
                                        <SparkleIcon />
                                    </span>
                                ))}
                            </div>
                            <span className={styles.kycCelebrationRing} aria-hidden="true" />
                            <span className={styles.kycCelebrationRing} aria-hidden="true" />
                            <span className={styles.kycCelebrationCheck}>
                                <svg
                                    viewBox="0 0 24 24"
                                    role="presentation"
                                    focusable="false"
                                >
                                    <path d="M5 12.5l4 4 10-10" />
                                </svg>
                            </span>
                        </div>
                        <h2 className={styles.kycCelebrationTitle}>
                            You&apos;re all set!
                        </h2>
                        <p className={styles.kycCelebrationText}>
                            Mutual fund KYC is complete. You can now continue with fund
                            selection and place your order.
                        </p>
                        <button
                            type="button"
                            className={styles.kycCelebrationCta}
                            onClick={handleClose}
                        >
                            Investor Profile Dashboard
                        </button>
                    </div>
                ) : (
                    <>
                        <div className={styles.kycDialogHeader}>
                            <span className={styles.kycDialogEyebrow}>MF readiness</span>
                            <h2>Check KYC status</h2>
                            <p>
                                We are checking the details needed to activate mutual fund
                                investing on your account.
                            </p>
                        </div>

                        <div className={styles.kycPhaseRail} aria-hidden="true">
                            {KYC_PHASES.map((phase, index) => {
                                const state = phaseStates[index];
                                const isActive = index === activePhaseIndex;
                                const dotClass =
                                    state === "complete"
                                        ? styles.kycPhaseDotComplete
                                        : state === "error"
                                            ? styles.kycPhaseDotError
                                            : isActive
                                                ? styles.kycPhaseDotActive
                                                : "";
                                return (
                                    <React.Fragment key={phase.key}>
                                        <div className={`${styles.kycPhaseDot} ${dotClass}`}>
                                            {state === "complete" ? (
                                                <svg viewBox="0 0 24 24" role="presentation" focusable="false">
                                                    <path d="M5 12.5l4 4 10-10" />
                                                </svg>
                                            ) : state === "error" ? (
                                                "!"
                                            ) : (
                                                index + 1
                                            )}
                                        </div>
                                        {index < KYC_PHASES.length - 1 && (
                                            <span
                                                className={`${styles.kycPhaseConnector} ${state === "complete" ? styles.kycPhaseConnectorFilled : ""
                                                    }`}
                                            />
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </div>

                        <div className={styles.kycSpotlight}>
                            <div
                                className={`${styles.kycSpotlightIcon} ${activePhaseState === "error" ? styles.kycSpotlightIconError : ""
                                    }`}
                            >
                                {activePhaseState === "running" ? (
                                    <span className={styles.kycSpotlightLoader} aria-label="Checking in progress">
                                        {Array.from({ length: 8 }).map((_, i) => (
                                            <span aria-hidden="true" key={i} style={{ ["--loader-index" as string]: i }} />
                                        ))}
                                    </span>
                                ) : activePhaseState === "error" ? (
                                    <span aria-hidden="true">!</span>
                                ) : (
                                    activePhase.icon
                                )}
                            </div>
                            <h3 className={styles.kycSpotlightTitle}>{activePhase.title}</h3>
                            <p className={styles.kycSpotlightText}>{activePhase.description}</p>

                            {isNomineeFormOpen && activePhase.steps.includes("nominee") && (
                                <form
                                    className={styles.kycNomineeForm}
                                    onSubmit={handleNomineeFormSubmit}
                                >
                                    <div className={styles.kycNomineeFormHeader}>
                                        <strong>Nominee details</strong>
                                        <span>
                                            {nomineeIsMinor
                                                ? "The existing nominee date of birth marks this nominee as a minor."
                                                : "The existing nominee date of birth marks this nominee as an adult."}
                                        </span>
                                    </div>
                                    <label>
                                        <span>Last 4 digits of Aadhaar</span>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            maxLength={4}
                                            pattern="[0-9]{4}"
                                            value={nomineeForm.aadhaarNumber}
                                            onChange={(event) =>
                                                setNomineeForm((current) => ({
                                                    ...current,
                                                    aadhaarNumber: getDigitsOnly(event.target.value, 4),
                                                }))
                                            }
                                            placeholder="1234"
                                            required
                                        />
                                    </label>
                                    {nomineeIsMinor && (
                                        <label>
                                            <span>Guardian name</span>
                                            <input
                                                type="text"
                                                value={nomineeForm.guardianName}
                                                onChange={(event) =>
                                                    setNomineeForm((current) => ({
                                                        ...current,
                                                        guardianName: event.target.value.replace(
                                                            /[^A-Za-z .'-]/g,
                                                            "",
                                                        ),
                                                    }))
                                                }
                                                pattern="[A-Za-z .'-]{2,}"
                                                required
                                            />
                                        </label>
                                    )}
                                    <div className={styles.kycNomineeFormGrid}>
                                        <label>
                                            <span>Email address</span>
                                            <input
                                                type="email"
                                                value={nomineeForm.emailAddress}
                                                onChange={(event) =>
                                                    setNomineeForm((current) => ({
                                                        ...current,
                                                        emailAddress: event.target.value.trim(),
                                                    }))
                                                }
                                                required
                                            />
                                        </label>
                                        <label>
                                            <span>Phone number</span>
                                            <input
                                                type="tel"
                                                inputMode="numeric"
                                                maxLength={10}
                                                value={nomineeForm.phoneNumber}
                                                onChange={(event) =>
                                                    setNomineeForm((current) => ({
                                                        ...current,
                                                        phoneNumber: getDigitsOnly(event.target.value, 10),
                                                    }))
                                                }
                                                pattern="[0-9]{10}"
                                                placeholder="9876543210"
                                                required
                                            />
                                        </label>
                                    </div>
                                    <label>
                                        <span>Address</span>
                                        <textarea
                                            value={nomineeForm.address}
                                            onChange={(event) =>
                                                setNomineeForm((current) => ({
                                                    ...current,
                                                    address: event.target.value,
                                                }))
                                            }
                                            rows={3}
                                            required
                                        />
                                    </label>
                                    <label>
                                        <span>PIN code</span>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            maxLength={6}
                                            value={nomineeForm.pinCode}
                                            onChange={(event) =>
                                                setNomineeForm((current) => ({
                                                    ...current,
                                                    pinCode: getDigitsOnly(event.target.value, 6),
                                                }))
                                            }
                                            pattern="[0-9]{6}"
                                            placeholder="400001"
                                            required
                                        />
                                    </label>
                                    <button type="submit">Continue KYC check</button>
                                </form>
                            )}

                            {errorMessage &&
                                errorStepKey &&
                                activePhase.steps.includes(errorStepKey) && (
                                    <span
                                        className={`${styles.kycDialogError} ${
                                            messageTone === "info" ? styles.kycDialogInfo : ""
                                        }`}
                                        role={messageTone === "info" ? "status" : "alert"}
                                    >
                                        {messageTone === "info" ? (
                                            <span className={styles.kycDialogInfoLoader}>
                                                <span
                                                    className={styles.kycDialogInfoSpinner}
                                                    aria-hidden="true"
                                                />
                                                <span>{errorMessage}</span>
                                            </span>
                                        ) : (
                                            <span>{errorMessage}</span>
                                        )}
                                        {!isRunning && (
                                            <span className={styles.kycDialogErrorActions}>
                                                {action?.url && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const openedWindow = openVerificationWindow(
                                                                action.url as string,
                                                            );
                                                            popupWindowRef.current = openedWindow;
                                                            setPopup((current) => ({
                                                                title: current?.title ?? action.label,
                                                                url: action.url as string,
                                                                opened: !!openedWindow,
                                                                kind:
                                                                    current?.kind ??
                                                                    (errorStepKey === "esign"
                                                                        ? "esign"
                                                                        : "signature"),
                                                                kycFormId: current?.kycFormId,
                                                            }));
                                                        }}
                                                    >
                                                        {action.label}
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={() => void runReadinessChecks()}
                                                >
                                                    Check status again
                                                </button>
                                            </span>
                                        )}
                                    </span>
                                )}
                        </div>
                    </>
                )}
                {popup && (
                    <div className={styles.kycPopup} role="dialog" aria-modal="true">
                        <div className={styles.kycPopupPanel}>
                            <div className={styles.kycPopupHeader}>
                                <strong>{popup.title}</strong>
                                <button
                                    type="button"
                                    onClick={() => {
                                        popupWindowRef.current?.close();
                                        popupWindowRef.current = null;
                                        setPopup(null);
                                    }}
                                    aria-label="Close verification popup"
                                >
                                    <CloseIcon fontSize="small" />
                                </button>
                            </div>
                            <div className={styles.kycPopupBody}>
                                <strong>
                                    {popup.opened
                                        ? "Verification opened in a separate secure popup."
                                        : "Verification popup was not opened."}
                                </strong>
                                <p>
                                    Complete the Finprim verification window, then return here and
                                    check status again. If you do not see the popup, allow popups
                                    for this site and reopen it.
                                </p>
                            </div>
                            <div className={styles.kycPopupActions}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        const openedWindow = openVerificationWindow(popup.url);
                                        popupWindowRef.current = openedWindow;
                                        setPopup((current) =>
                                            current ? { ...current, opened: !!openedWindow } : current,
                                        );
                                    }}
                                >
                                    Reopen verification
                                </button>
                                <button
                                    type="button"
                                    onClick={() => void handlePopupCompleted(true)}
                                >
                                    I have completed this
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Dialog>
    );
};

export default KycCheckDialog;
